import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../store/auth.ts';

export const api: AxiosInstance = axios.create({ baseURL: '/api', withCredentials: true });

api.interceptors.request.use((cfg) => {
  const t = useAuthStore.getState().accessToken;
  if (t && cfg.headers) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// Recovery classification for a 401. A session must end ONLY when the refresh
// token is genuinely dead (revoked/expired) AND ambient SSO can't rescue it —
// never on a transient network/5xx blip, and never on a rotation race (the
// backend now tolerates rotated tokens within a grace window).
type Recovery = 'recovered' | 'dead' | 'transient';

async function recoverSession(): Promise<Recovery> {
  const store = useAuthStore.getState();
  // No local refresh token at all → nothing to refresh; try ambient SSO, else dead.
  if (!store.refreshToken) {
    return (await store.ssoBootstrap(true)) ? 'recovered' : 'dead';
  }
  try {
    await store.refresh();
    return 'recovered';
  } catch (e) {
    const status = (e as { response?: { status?: number } })?.response?.status;
    if (status === 401) {
      // Refresh token genuinely revoked/expired — try the ambient wap_sso cookie
      // before declaring the session dead.
      return (await useAuthStore.getState().ssoBootstrap(true)) ? 'recovered' : 'dead';
    }
    // Network error / 5xx / no response → transient. Do NOT log the user out.
    return 'transient';
  }
}

let refreshing: Promise<Recovery> | null = null;
api.interceptors.response.use(
  (r) => r,
  async (err: { response?: { status?: number }; config?: InternalAxiosRequestConfig & { _retry?: boolean } }) => {
    const status = err.response?.status;
    const original = err.config;
    if (status === 401 && original && !original._retry) {
      original._retry = true;
      refreshing ??= recoverSession().finally(() => { refreshing = null; });
      const outcome = await refreshing;
      if (outcome === 'recovered') return api(original);
      if (outcome === 'dead') {
        useAuthStore.setState({ accessToken: null, refreshToken: null, user: null });
        if (typeof window !== 'undefined') window.location.href = '/login';
      }
      // 'transient' → fall through and reject; the session stays signed in.
    }
    throw err;
  },
);
